import classNames from 'classnames'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Separator } from 'components/common/Separator'
import { Tag } from 'components/common/Tag'
import { MobileExtendedNavSectionSkeleton } from 'components/nav/ExtendedNavSectionSkeleton'
import { NavItem } from 'components/nav/v2/NavItem'
import routes from 'constants/routes'
import { useArtistsNavigationItems } from 'hooks/nav/useArtistsNavigationItems'
import useTranslation from 'next-translate/useTranslation'
import { ContainerElement } from 'types'
import { titleCase } from 'utils/strings'

export function ArtistsMobileNav() {
  const { t } = useTranslation('common')
  const { blueChipArtists, mostPopularArtists, trendingArtists } =
    useArtistsNavigationItems()

  return (
    <div className='h-full overflow-y-auto bg-cool-50 px-6 py-5'>
      <Button
        className='w-full space-x-2 text-xs'
        pill
        style='solid-primary'
        type='link'
        href={routes.artists}
        size='md'
      >
        <span>{titleCase(t('view_all_artists'))}</span>
        <Icon name='forward-arrow' />
      </Button>

      <Separator className='my-4' color='cool-400' />

      <Section label={t('extended_nav.blue_chip_artists')}>
        {!blueChipArtists && <MobileExtendedNavSectionSkeleton />}
        {blueChipArtists?.map(artist => (
          <NavItem
            key={artist.href}
            href={artist.href}
            size='custom'
            className='justify-between py-1 text-sm'
          >
            <span>{artist.name}</span>
            <Tag color='light-purple' size='S' icon={null}>
              {t('extended_nav.x_works', { x: artist.totalArtworks })}
            </Tag>
          </NavItem>
        ))}
      </Section>

      <Separator className='my-4' color='cool-400' />

      <Section label={t('extended_nav.most_popular_artists')}>
        {!mostPopularArtists && <MobileExtendedNavSectionSkeleton />}
        {mostPopularArtists?.map(artist => (
          <NavItem
            key={artist.href}
            href={artist.href}
            size='custom'
            className='justify-between py-1 text-sm'
          >
            <span>{artist.name}</span>
            <Tag color='light-purple' size='S' icon={null}>
              {t('extended_nav.x_works', { x: artist.totalArtworks })}
            </Tag>
          </NavItem>
        ))}
      </Section>

      <Separator className='my-4' color='cool-400' />

      <Section label={t('extended_nav.trending_artists')}>
        {!trendingArtists && <MobileExtendedNavSectionSkeleton />}
        {trendingArtists?.map(artist => (
          <NavItem
            key={artist.href}
            href={artist.href}
            size='custom'
            className='justify-between py-1 text-sm'
          >
            <span>{artist.name}</span>
            <Tag size='S' color='success-light' icon='arrow-up'>
              {artist.trendingPercent}%
            </Tag>
          </NavItem>
        ))}
      </Section>
    </div>
  )
}

interface SectionProps extends ContainerElement {
  label: string
}

function Section({ label, children, className }: SectionProps) {
  return (
    <nav className={classNames(className)}>
      <p className='mb-1 text-sm font-medium'>{label}</p>
      {children}
    </nav>
  )
}
